import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Zap, Tv, Smartphone, MonitorSmartphone, ChevronDown } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { scrollToSection, HERO_IMG } from "@/utils/scroll";

const lineAnim = (delay) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const { content } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  if (!content) return <section ref={ref} className="min-h-screen" />;
  const s = content.settings;

  return (
    <section ref={ref} className="hero-bg noise relative flex min-h-screen items-center overflow-hidden pt-32 pb-20">
      <div className="grid-lines absolute inset-0" aria-hidden="true" />
      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          {/* left */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-electric/30 bg-electric/5 px-4 py-1.5 shadow-[0_0_18px_rgba(0,240,255,0.15)]"
            >
              <span className="h-2 w-2 animate-pulse rounded-full bg-electric" />
              <span className="font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-electric">
                Premium Streaming
              </span>
            </motion.div>

            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              <span className="mask-line">
                <motion.span className="block" {...lineAnim(0.1)}>
                  Your Entertainment.
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span className="text-gradient block" {...lineAnim(0.24)}>
                  One Powerful Hub.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mt-6 max-w-md text-base leading-relaxed text-slate-500 sm:text-lg"
            >
              {s.hero_sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.58 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-view-plans-button"
                onClick={() => scrollToSection("#plans")}
                className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 text-base"
              >
                View Plans <ArrowRight className="h-4 w-4" />
              </button>
              <button
                data-testid="hero-contact-us-button"
                onClick={() => scrollToSection("#contact")}
                className="btn-ghost inline-flex items-center rounded-full px-8 py-4 text-base"
              >
                Contact Us
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold text-slate-500"
            >
              {["HD • Full HD Quality", "Instant Setup", "24/7 Support"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-electric" /> {t}
                </span>
              ))}
            </motion.div>
          </div>

          {/* right — 3D TV visual */}
          <div className="relative lg:col-span-6">
            <motion.div
              style={{ y: imgY }}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-10 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,112,243,0.14),transparent_65%)] blur-3xl" aria-hidden="true" />
              <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} className="relative">
                <img
                  src={HERO_IMG}
                  alt="Premium streaming experience on a modern TV with glowing blue light"
                  loading="eager"
                  className="relative w-full rounded-3xl border border-electric/25 shadow-[0_30px_80px_rgba(2,6,23,0.18)]"
                  data-testid="hero-tv-visual"
                />
                <div className="glass absolute left-4 top-4 flex items-center gap-2 rounded-full px-4 py-2" data-testid="hero-live-badge">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                  <span className="font-mono text-[11px] font-bold tracking-[0.2em] text-slate-900">LIVE • HD</span>
                </div>
                <div className="glass absolute inset-x-4 bottom-4 hidden items-center justify-between rounded-2xl px-5 py-3.5 sm:flex" data-testid="hero-device-strip">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <Tv className="h-4 w-4 text-electric" /> Smart TV
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <Smartphone className="h-4 w-4 text-electric" /> Mobile
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <MonitorSmartphone className="h-4 w-4 text-electric" /> All devices
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollToSection("#services")}
        data-testid="hero-scroll-cue"
        aria-label="Scroll down"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ opacity: { delay: 1.4, duration: 0.6 }, y: { repeat: Infinity, duration: 2 } }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-slate-500 hover:text-electric"
      >
        <ChevronDown className="h-6 w-6" />
      </motion.button>
    </section>
  );
}
